<template>
  <div class="flex flex-col">
    <form class="max-w-md" @submit.prevent="saveUploadLimit">
      <div class="flex flex-wrap items-end gap-3">
        <div class="min-w-[120px]">
          <label class="text-sm text-gray block mb-1">MB</label>
          <input
            v-model.number="uploadForm.sizeInMB"
            type="number"
            min="0.1"
            step="0.1"
            class="w-full px-3 py-2 text-sm border border-gray-300 rounded"
            :disabled="uploadLoading"
          />
        </div>
        <div v-if="uploadMeta.bytes != null" class="text-xs text-gray pb-2">
          {{ formatBytes(uploadMeta.bytes) }}
          <span v-if="uploadMeta.source"> · {{ uploadMeta.source }}</span>
        </div>
        <button
          type="submit"
          class="btn-primary px-3 py-2 rounded-sm text-white"
          :disabled="uploadSaving || uploadLoading"
        >
          Save
          <i v-if="uploadSaving" class="fa fa-spinner fa-spin ml-1"></i>
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import {
  extractErrorMessage,
  fetchMaxUploadSize,
  saveMaxUploadSize,
} from "./gatewayApi";
import { useToast } from "@/composables";

const { errorToast, successToast, warningToast } = useToast();

const uploadLoading = ref(false);
const uploadSaving = ref(false);
const uploadForm = ref<{ sizeInMB: number | null }>({ sizeInMB: null });
const uploadMeta = ref<{ bytes: number | null; source: string | null }>({ bytes: null, source: null });

const formatBytes = (bytes: number) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const loadUploadLimit = async () => {
  uploadLoading.value = true;
  try {
    const config = await fetchMaxUploadSize();
    if (config.mb != null) uploadForm.value.sizeInMB = config.mb;
    uploadMeta.value = { bytes: config.bytes, source: config.source };
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not load upload limit"));
  } finally {
    uploadLoading.value = false;
  }
};

const saveUploadLimit = async () => {
  const mb = Number(uploadForm.value.sizeInMB);
  if (!Number.isFinite(mb) || mb <= 0) {
    warningToast("Validation", "Enter a valid MB value");
    return;
  }
  uploadSaving.value = true;
  try {
    await saveMaxUploadSize(mb);
    successToast("Success", "Upload limit saved");
    await loadUploadLimit();
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not save upload limit"));
  } finally {
    uploadSaving.value = false;
  }
};

defineExpose({ refresh: loadUploadLimit });
</script>
